import { apiClient } from '@/lib/apiClient';
import { supabase } from '@/lib/supabaseClient';
import { FAQItem } from '@/types/faq';

export interface CreateVideoData {
  title: string;
  description: string;
  videoUrl: string;
  videoType: 'youtube' | 'self-hosted';
  category: string;
  section?: string;
  tags: string[];
  isGlobal?: boolean;
}

export interface UpdateVideoData extends CreateVideoData {
  id: string;
}

export interface Video {
  id: string;
  title: string;
  description: string;
  video_url: string;
  video_type: 'youtube' | 'self-hosted';
  category: string;
  section: string;
  tags: string[];
  is_active: boolean;
  is_global?: boolean;
  tenant_id?: string;
  created_at: string;
  updated_at?: string;
}

export class VideoService {
  /**
   * Get all videos
   */
  static async getVideos(section?: string): Promise<FAQItem[]> {
    try {

      const params: Record<string, any> = {};
      if (section) {
        params.category = section;
      }

      const data = await apiClient.get<Video[]>('/api/v1/ai/videos', params);


      // Transform API response to match FAQItem interface
      return (data || []).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        videoUrl: video.video_url,
        videoType: video.video_type,
        category: video.category,
        section: video.section,
        tags: video.tags || [],
        createdAt: video.created_at
      }));
    } catch (error) {
      console.error('Error in getVideos via API:', error);
      throw error;
    }
  }

  /**
   * Get a single video by ID
   */
  static async getVideo(id: string): Promise<FAQItem | null> {
    try {
      const data = await apiClient.get<Video>(`/api/v1/ai/videos/${id}`);

      if (!data) return null;

      // Transform API response to match FAQItem interface
      return {
        id: data.id,
        title: data.title,
        description: data.description,
        videoUrl: data.video_url,
        videoType: data.video_type,
        category: data.category,
        section: data.section,
        tags: data.tags || [],
        createdAt: data.created_at,
        isGlobal: data.is_global || false
      };
    } catch (error) {
      console.error('Error in getVideo via API:', error);
      throw error;
    }
  }

  /**
   * Create a new video in Supabase
   */
  static async createVideo(videoData: CreateVideoData): Promise<FAQItem> {
    try {
      // Get current user to determine tenant_id
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's tenant_id from the users table
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw profileError;
      }

      const { data, error } = await supabase
        .from('videos')
        .insert({
          title: videoData.title,
          description: videoData.description,
          video_url: videoData.videoUrl,
          video_type: videoData.videoType,
          category: videoData.category,
          section: videoData.section || 'faq',
          tags: videoData.tags || [],
          is_active: true,
          tenant_id: userProfile.tenant_id,
          is_global: videoData.isGlobal || false
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating video in Supabase:', error);
        throw error;
      }

      return {
        id: data.id,
        title: data.title,
        description: data.description,
        videoUrl: data.video_url,
        videoType: data.video_type,
        category: data.category,
        section: data.section,
        tags: data.tags || [],
        createdAt: data.created_at,
        isGlobal: data.is_global || false
      };
    } catch (error) {
      console.error('Error in createVideo via Supabase:', error);
      throw error;
    }
  }

  /**
   * Update an existing video
   */
  static async updateVideo(videoData: UpdateVideoData): Promise<FAQItem> {
    try {
      const { data, error } = await supabase
        .from('videos')
        .update({
          title: videoData.title,
          description: videoData.description,
          video_url: videoData.videoUrl,
          video_type: videoData.videoType,
          category: videoData.category,
          section: videoData.section || 'faq',
          tags: videoData.tags || [],
          is_global: videoData.isGlobal || false,
          updated_at: new Date().toISOString()
        })
        .eq('id', videoData.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating video in Supabase:', error);
        throw error;
      }

      return {
        id: data.id,
        title: data.title,
        description: data.description,
        videoUrl: data.video_url,
        videoType: data.video_type,
        category: data.category,
        section: data.section,
        tags: data.tags || [],
        createdAt: data.created_at,
        isGlobal: data.is_global || false
      };
    } catch (error) {
      console.error('Error in updateVideo via Supabase:', error);
      throw error;
    }
  }

  /**
   * Delete a video from Supabase
   */
  static async deleteVideo(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting video from Supabase:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteVideo via Supabase:', error);
      throw error;
    }
  }

  /**
   * Search videos by title or description
   */
  static async searchVideos(query: string, section?: string): Promise<FAQItem[]> {
    try {
      const params: Record<string, any> = { search: query };
      if (section) {
        params.category = section;
      }

      const data = await apiClient.get<Video[]>('/api/v1/ai/videos', params);

      // Transform API response to match FAQItem interface
      return (data || []).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        videoUrl: video.video_url,
        videoType: video.video_type,
        category: video.category,
        section: video.section,
        tags: video.tags || [],
        createdAt: video.created_at
      }));
    } catch (error) {
      console.error('Error in searchVideos via API:', error);
      throw error;
    }
  }

  /**
   * Get videos by category
   */
  static async getVideosByCategory(category: string, section?: string): Promise<FAQItem[]> {
    try {
      const params: Record<string, any> = { category };
      if (section) {
        params.section = section;
      }

      const data = await apiClient.get<Video[]>('/api/v1/ai/videos', params);

      // Transform API response to match FAQItem interface
      return (data || []).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        videoUrl: video.video_url,
        videoType: video.video_type,
        category: video.category,
        section: video.section,
        tags: video.tags || [],
        createdAt: video.created_at
      }));
    } catch (error) {
      console.error('Error in getVideosByCategory via API:', error);
      throw error;
    }
  }

  /**
   * Get video statistics
   */
  static async getVideoStats(section?: string): Promise<{
    total: number;
    byCategory: { [key: string]: number };
    byType: { [key: string]: number };
    bySection: { [key: string]: number };
  }> {
    try {
      const params: Record<string, any> = { include_stats: true };
      if (section) {
        params.category = section;
      }

      // Get all videos with stats and calculate the statistics ourselves
      const videos = await apiClient.get<Video[]>('/api/v1/ai/videos', params);

      // Calculate stats from the video data
      const stats = {
        total: videos.length,
        byCategory: {} as { [key: string]: number },
        byType: {} as { [key: string]: number },
        bySection: {} as { [key: string]: number }
      };

      videos.forEach(video => {
        // Count by category
        if (video.category) {
          stats.byCategory[video.category] = (stats.byCategory[video.category] || 0) + 1;
        }

        // Count by type
        if (video.video_type) {
          stats.byType[video.video_type] = (stats.byType[video.video_type] || 0) + 1;
        }

        // Count by section (if it exists)
        if (video.section) {
          stats.bySection[video.section] = (stats.bySection[video.section] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error in getVideoStats via API:', error);
      throw error;
    }
  }

  /**
   * Validate YouTube URL and extract video ID
   */
  static validateYouTubeUrl(url: string): { isValid: boolean; videoId?: string } {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    
    return {
      isValid: !!match,
      videoId: match ? match[1] : undefined
    };
  }

  /**
   * Get videos by section from Supabase
   */
  static async getVideosBySection(section: string): Promise<FAQItem[]> {
    try {
      // Get current user to determine tenant_id
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's tenant_id from the users table
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw profileError;
      }

      // Fetch global videos OR videos for this tenant
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('section', section)
        .eq('is_active', true)
        .or(`is_global.eq.true,tenant_id.eq.${userProfile.tenant_id}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching videos from Supabase:', error);
        throw error;
      }

      // Transform Supabase response to match FAQItem interface
      return (data || []).map(video => ({
        id: video.id,
        title: video.title,
        description: video.description,
        videoUrl: video.video_url,
        videoType: video.video_type,
        category: video.category,
        section: video.section,
        tags: video.tags || [],
        createdAt: video.created_at,
        isGlobal: video.is_global || false
      }));
    } catch (error) {
      console.error('Error in getVideosBySection from Supabase:', error);
      throw error;
    }
  }

  /**
   * Validate self-hosted video URL
   */
  static validateSelfHostedUrl(url: string): { isValid: boolean } {
    // Basic validation for self-hosted URLs
    const validExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const hasValidExtension = validExtensions.some(ext => url.toLowerCase().includes(ext));
    const hasValidPath = url.startsWith('/') || url.startsWith('http');
    
    return {
      isValid: hasValidExtension && hasValidPath
    };
  }
}